// verify.service.ts

import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccessTokenResult, VerifyFactorInfo, VerifyFactorUserInformation } from '../models/registration';
import { environment } from '../../environments/environment';
import { HttpErrorHandler, HandleError } from './http-error-handler.service';
import { catchError, retry } from 'rxjs/operators';
import { Router, ActivatedRoute, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { SessionService } from './session.service';
import { LogService } from './log.service';
import { ContactEmailInformation } from '../models/contact';

@Injectable({
    providedIn: 'root'
})
export class VerifyService {


    private _logging = environment.logging;
    private _apiSettings = environment.restAPI;
    private _baseServiceUrl = '';
    private _verifyFactorInfo: VerifyFactorInfo
    private _handleError: HandleError;

    private verifyInfoSessionName = 'verifyInfo';
    verifyFactorInfoSubject: BehaviorSubject<VerifyFactorInfo>;
    verifyFactorInfo: Observable<VerifyFactorInfo>;

    constructor(@Inject('BASE_URL') baseUrl: string,
        private _router: Router,
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _sessionService: SessionService,
        private _logger: LogService,
        httpErrorHandler: HttpErrorHandler) {
        this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
        this._handleError = httpErrorHandler.createHandleError('VerifyService');

        const sessionVerifyFactorInfo: VerifyFactorInfo = this._sessionService.getObjectFromSession(this.verifyInfoSessionName);
        if (sessionVerifyFactorInfo !== null) {
            sessionVerifyFactorInfo.sessionCreateDate = new Date(sessionVerifyFactorInfo.sessionCreateDate);
            sessionVerifyFactorInfo.sessionLastActiveDate = new Date(sessionVerifyFactorInfo.sessionLastActiveDate);

            if (this._logging.userTraceLog) {
                console.log('VerifyService.constructor :: getObjectFromSession = true');
            }

            this._verifyFactorInfo = sessionVerifyFactorInfo;
        } else {
            if (this._logging.userTraceLog) {
                console.log('VerifyService.constructor :: getObjectFromSession = false');
            }

            this._verifyFactorInfo = this._getNewVerifyInfo();
        }

        if (this._logging.userTraceLog) {
            console.log('VerifyService.constructor :: _verifyFactorInfo = ' + JSON.stringify(this._verifyFactorInfo));
        }

        this.verifyFactorInfoSubject = new BehaviorSubject<VerifyFactorInfo>(this._verifyFactorInfo);
        this.verifyFactorInfo = this.verifyFactorInfoSubject.asObservable();

        this.verifyFactorInfo.subscribe(response => this._onVerifyFactorInfoChanged(response), error => this._onError(error));

    }

    private _onVerifyFactorInfoChanged(newVerifyFactorInfo: VerifyFactorInfo): void {
        this._sessionService.setObjectToSession(this.verifyInfoSessionName, newVerifyFactorInfo);

        if (this._logging.userTraceLog) {
            console.log('VerifyService.onUserInfoChanged :: newVerifyFactorInfo = ' + JSON.stringify(newVerifyFactorInfo))
        }
    }

    private _onError(error: any) {
        if (this._logging.errorTraceLogging) {
            this._logger.log("VerifyService", "_onError", "JSON.stringify(error)", "");
        }
        console.log(error);
    }

    public _getNewVerifyInfo(): VerifyFactorInfo {
        const newVerifyFactorInfo: VerifyFactorInfo = {
            userName: null,
            participantId: null,
            companyCode: null,
            ipAddress: null,
            homeEmail: null,
            verificationCode: null,
            gracePeriodExpired: false,
            gracePeriodSkipCount: null,
            multifactorEligible: false,
            multifactorEnabled: false,
            multifactorGracePeriod: null,
            lastVerifiedIpAddress: null,
            lastVerifiedOTP: null,
            multifactorRetryCount: null,
            skipEnrollment: false,
            sessionToken: null,
            sessionCreateDate: null,
            sessionLastActiveDate: null,
            sessionTimeout: false,
            resend: false
            ,mfaChallengeToken: null
        };

        return newVerifyFactorInfo;
    }

    // setParticipantId(data: any) {
    //     this.participantId = data;
    // }

    getParticipantId(): number {
        return this._verifyFactorInfo.participantId !== null && this._verifyFactorInfo.participantId > 0 ? this._verifyFactorInfo.participantId : null;
    }

    // setUserName(data: any) {
    //     this.userName = data;
    // }

    getUserName(): string {
        return this._verifyFactorInfo.userName !== null && this._verifyFactorInfo.userName.length > 0 ? this._verifyFactorInfo.userName : null;
    }

    setEmailAddress(data: any) {
        this._verifyFactorInfo.homeEmail = data;
        this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
    }

    getEmailAddress(): string {
        return this._verifyFactorInfo.homeEmail !== null && this._verifyFactorInfo.homeEmail.length > 0 ? this._verifyFactorInfo.homeEmail : null;
    }

    getCompanyCode():string {
        return this._verifyFactorInfo.companyCode !== null && this._verifyFactorInfo.companyCode.length > 0 ? this._verifyFactorInfo.companyCode : null;
    }

    setMultifactorEnabled(data: boolean) {
        this._verifyFactorInfo.multifactorEnabled = data;
        this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
    }

    setSkipEnrollment(data: boolean) {
        this._verifyFactorInfo.skipEnrollment = data;
        this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
    }

    setLastVerifiedOTP(data: Date) {
        this._verifyFactorInfo.lastVerifiedOTP = data;
        this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
    }

    setLastVerifiedIpAddress(data: string) {
        this._verifyFactorInfo.lastVerifiedIpAddress = data;
        this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
    }

    setCurrentIPAddress(data: string) {
        this._verifyFactorInfo.ipAddress = data;
        this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
    }

    getCurrentIPAddress():string {
        return this._verifyFactorInfo.ipAddress;
    }

    setVerify(
        participantId: number,
        userName: string,
        companyCode: string,
        multifactorEligible: boolean,
        multifactorEnabled: boolean,
        multifactorGracePeriod: number,
        lastVerifiedIpAddress: string,
        lastVerifiedOTP: Date,
        multifactorRetryCount: number,
        currentIpAddress: string,
        skipEnrollment: boolean,
        mfaChallengeToken: string

    ) :void {

        if (userName !== null && userName.length > 0
            && participantId !== null && participantId > 0) {

            this._verifyFactorInfo = this._getNewVerifyInfo();

            this._verifyFactorInfo.participantId = participantId;
            this._verifyFactorInfo.userName = userName;
            this._verifyFactorInfo.companyCode = companyCode;
            this._verifyFactorInfo.multifactorEligible = multifactorEligible;
            this._verifyFactorInfo.multifactorEnabled = multifactorEnabled;
            this._verifyFactorInfo.lastVerifiedIpAddress = lastVerifiedIpAddress;
            this._verifyFactorInfo.lastVerifiedOTP = lastVerifiedOTP;
            this._verifyFactorInfo.gracePeriodSkipCount = multifactorGracePeriod;
            this._verifyFactorInfo.multifactorRetryCount = multifactorRetryCount;
            this._verifyFactorInfo.skipEnrollment = skipEnrollment;
            this._verifyFactorInfo.mfaChallengeToken = mfaChallengeToken;
            this._verifyFactorInfo.sessionCreateDate = new Date();
            this._verifyFactorInfo.sessionLastActiveDate = new Date();
            this.verifyFactorInfoSubject.next(this._verifyFactorInfo);
        }
    }

    public sendVerificationCode(resendFlag:boolean): Observable<number> {

        this._verifyFactorInfo.participantId = this.getParticipantId();
        this._verifyFactorInfo.userName = this.getUserName();
        this._verifyFactorInfo.companyCode = this.getCompanyCode();
        this._verifyFactorInfo.resend = resendFlag;

        const serviceUrl: string = this._baseServiceUrl + 'login/sendverificationcode';

        if (this._logging.serviceCallTraceLog) {
            this._logger.log("VerifyService", "sendVerificationCode", "Calling send verification code.", this._verifyFactorInfo);
        }

        // return of 0 equals success
        return this._httpClient.post<number>(serviceUrl, this._verifyFactorInfo).pipe(
            retry(this._apiSettings.retryCount),
            catchError(this._handleError('sendVerificationCode', null))
        );
    }

    public getMfaContactEmailInformation(): Observable<ContactEmailInformation> {
        const serviceUrl: string = this._baseServiceUrl + 'login/getmfacontactemailinformation';
        return this._httpClient.post<ContactEmailInformation>(serviceUrl, {
            mfaChallengeToken: this._verifyFactorInfo.mfaChallengeToken
        }).pipe(
            catchError(this._handleError('getMfaContactEmailInformation', null))
        );
    }

    public checkVerificationCode(verificationCode: string): Observable<VerifyFactorUserInformation> {

        // send parameters to service
        this._verifyFactorInfo.participantId = this.getParticipantId();
        this._verifyFactorInfo.userName = this.getUserName();
        this._verifyFactorInfo.companyCode = this.getCompanyCode();
        this._verifyFactorInfo.verificationCode = verificationCode;
        this._verifyFactorInfo.ipAddress = this.getCurrentIPAddress();

        if (this._logging.serviceCallTraceLog) {
            this._logger.log("VerifyService", "checkVerificationCode", "Checking verification code trace start.", this._verifyFactorInfo);
        }

        const serviceUrl: string = this._baseServiceUrl + 'login/checkverificationcode';
        // return greater than 0 equals success
        return this._httpClient.post<VerifyFactorUserInformation>(serviceUrl, this._verifyFactorInfo).pipe(
            retry(this._apiSettings.retryCount),
            catchError(this._handleError('checkVerificationCode', null))
        );
    }

    public skipMultifactorEnrollment(): Observable<AccessTokenResult> {
        const serviceUrl: string = this._baseServiceUrl + 'login/skipmultifactorenrollment';
        return this._httpClient.post<AccessTokenResult>(serviceUrl, {
            mfaChallengeToken: this._verifyFactorInfo.mfaChallengeToken
        }).pipe(
            catchError(this._handleError('skipMultifactorEnrollment', null))
        );
    }

    private verifyExpired(): boolean {

        let expired: boolean = false;

        const maxVerifyOTPDays: number = 90;
        const now: Date = new Date();
        const dateLastVerified = new Date(this._verifyFactorInfo.lastVerifiedOTP)
        let daysSinceLastVerify = (now.getTime() - dateLastVerified.getTime()) / (1000 * 3600 * 24)

        if (this._verifyFactorInfo.lastVerifiedIpAddress !== this._verifyFactorInfo.ipAddress) {
            expired = true
        } else
        {
            expired = daysSinceLastVerify > maxVerifyOTPDays;
        }

        return expired;

    }

    isInGracePeriod(): boolean {

        // user has already enrolled
        if (this._verifyFactorInfo.multifactorEnabled
            && this._verifyFactorInfo.lastVerifiedOTP !== null
            && this._verifyFactorInfo.lastVerifiedIpAddress !== null) {
            return false;
        }

        //user has not enabled and skip count is not == 999 (new registrant)
        if (!this._verifyFactorInfo.multifactorEnabled && (this._verifyFactorInfo.gracePeriodSkipCount == null ||
            this._verifyFactorInfo.gracePeriodSkipCount > 0
            && this._verifyFactorInfo.gracePeriodSkipCount != 999 )) {
            return true;
        }

        return false;
    }


    // if in grace period, skip
    requiresVerification(): boolean {

        if (this._verifyFactorInfo.multifactorEligible)
        {
            if (this.isInGracePeriod() && this._verifyFactorInfo.skipEnrollment) {
                return false;
            }
            else if (this.isInGracePeriod() && !this._verifyFactorInfo.skipEnrollment) {
                return true;
            }
            else {
                if (this._verifyFactorInfo.multifactorEnabled) {
                    return this.verifyExpired();
                }
                return true;
            }
        }

        return false;

    }
}
